import { z } from 'zod';
import { type Json, JsonValue } from './json';

/**
 * Filter AST (SPEC §6). Used by views, permissions, workflow conditions, rollup filters and the API.
 *
 *   { "and": [
 *     { "field": "stage", "op": "in", "value": ["proposal", "negotiation"] },
 *     { "field": "company.industry", "op": "eq", "value": "logistics" },
 *     { "or": [ { "field": "lead_score", "op": "gte", "value": 50 }, { "field": "owner", "op": "is_empty" } ] }
 *   ]}
 *
 * `field` is a field api key or a relation path (`company.industry`), or a system column
 * (`id`, `created_at`, `updated_at`, `created_by`, `updated_by`).
 */

/** Special values: `{"$var":"current_member"}`, `{"$var":"current_member.team"}`, `{"$rel":"-30d"}`. */
export type FilterVariable = { $var: string };
export type FilterRelative = { $rel: string };
export type FilterValue = Json | FilterVariable | FilterRelative | FilterValue[];

export type FilterCondition = { field: string; op: string; value?: FilterValue };
export type FilterAnd = { and: FilterNode[] };
export type FilterOr = { or: FilterNode[] };
export type FilterNot = { not: FilterNode };
export type FilterNode = FilterCondition | FilterAnd | FilterOr | FilterNot;

export const FILTER_MAX_DEPTH = 8;
export const FILTER_MAX_CONDITIONS = 200;

export const FilterVariableSchema = z.object({ $var: z.string().min(1).max(100) }).strict();
export const FilterRelativeSchema = z.object({ $rel: z.string().min(1).max(60) }).strict();

export const FilterValueSchema: z.ZodType<FilterValue> = z.lazy(() =>
  z.union([FilterVariableSchema, FilterRelativeSchema, z.array(FilterValueSchema), JsonValue]),
);

export const FieldPath = z
  .string()
  .min(1)
  .max(300)
  .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)*$/, { error: 'Expected a field key or relation path' });

export const FilterConditionSchema = z
  .object({ field: FieldPath, op: z.string().min(1).max(40), value: FilterValueSchema.optional() })
  .strict();

const FilterNodeInner: z.ZodType<FilterNode> = z.lazy(() =>
  z.union([
    FilterConditionSchema,
    z.object({ and: z.array(FilterNodeInner).max(FILTER_MAX_CONDITIONS) }).strict(),
    z.object({ or: z.array(FilterNodeInner).max(FILTER_MAX_CONDITIONS) }).strict(),
    z.object({ not: FilterNodeInner }).strict(),
  ]),
);

export const FilterNodeSchema: z.ZodType<FilterNode> = FilterNodeInner.superRefine((node, ctx) => {
  const stats = measureFilter(node);
  if (stats.depth > FILTER_MAX_DEPTH) {
    ctx.addIssue({ code: 'custom', message: `Filter nesting exceeds ${FILTER_MAX_DEPTH} levels` });
  }
  if (stats.conditions > FILTER_MAX_CONDITIONS) {
    ctx.addIssue({ code: 'custom', message: `Filter has more than ${FILTER_MAX_CONDITIONS} conditions` });
  }
});

export function isCondition(node: FilterNode): node is FilterCondition {
  return 'field' in node;
}
export function isAnd(node: FilterNode): node is FilterAnd {
  return 'and' in node;
}
export function isOr(node: FilterNode): node is FilterOr {
  return 'or' in node;
}
export function isNot(node: FilterNode): node is FilterNot {
  return 'not' in node;
}
export function isVariable(value: unknown): value is FilterVariable {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && '$var' in value;
}
export function isRelative(value: unknown): value is FilterRelative {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && '$rel' in value;
}

export function measureFilter(node: FilterNode, depth = 1): { depth: number; conditions: number } {
  if (isCondition(node)) return { depth, conditions: 1 };
  const children = isNot(node) ? [node.not] : isAnd(node) ? node.and : node.or;
  let maxDepth = depth;
  let conditions = 0;
  for (const child of children) {
    const s = measureFilter(child, depth + 1);
    maxDepth = Math.max(maxDepth, s.depth);
    conditions += s.conditions;
  }
  return { depth: maxDepth, conditions };
}

/** Combine filters with AND, dropping empty ones. */
export function andFilters(...nodes: Array<FilterNode | null | undefined>): FilterNode | null {
  const present = nodes.filter((n): n is FilterNode => n != null);
  if (present.length === 0) return null;
  if (present.length === 1) return present[0] ?? null;
  return { and: present };
}

/** Collect every field path referenced by a filter. */
export function filterFieldPaths(node: FilterNode | null | undefined, out = new Set<string>()): Set<string> {
  if (!node) return out;
  if (isCondition(node)) out.add(node.field);
  else if (isNot(node)) filterFieldPaths(node.not, out);
  else for (const child of isAnd(node) ? node.and : node.or) filterFieldPaths(child, out);
  return out;
}

// ---------------------------------------------------------------------------------------------
// Relative dates: "today", "-30d", "+2w", "start_of_quarter", "start_of_month-1m", "now-6h"
// ---------------------------------------------------------------------------------------------

const REL_RE =
  /^(now|today|start_of_(?:week|month|quarter|year)|end_of_(?:week|month|quarter|year))?((?:[+-]\d{1,4}[hdwmqy])*)$/;

export function isValidRelativeDate(expr: string): boolean {
  return expr.length > 0 && REL_RE.test(expr);
}

/** Resolve a relative date expression against `now` (UTC calendar). Throws on invalid input. */
export function resolveRelativeDate(expr: string, now: Date = new Date()): Date {
  const m = REL_RE.exec(expr);
  if (!m || expr.length === 0) throw new Error(`Invalid relative date "${expr}"`);
  const base = m[1] ?? (m[2] ? 'today' : 'now');
  let d = new Date(now.getTime());
  const startOfDay = (x: Date) => new Date(Date.UTC(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate()));
  switch (base) {
    case 'now':
      break;
    case 'today':
      d = startOfDay(d);
      break;
    case 'start_of_week': {
      const s = startOfDay(d);
      const dow = (s.getUTCDay() + 6) % 7; // Monday = 0
      d = new Date(s.getTime() - dow * 86_400_000);
      break;
    }
    case 'end_of_week': {
      const s = startOfDay(d);
      const dow = (s.getUTCDay() + 6) % 7;
      d = new Date(s.getTime() + (6 - dow) * 86_400_000);
      break;
    }
    case 'start_of_month':
      d = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
      break;
    case 'end_of_month':
      d = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
      break;
    case 'start_of_quarter':
      d = new Date(Date.UTC(d.getUTCFullYear(), Math.floor(d.getUTCMonth() / 3) * 3, 1));
      break;
    case 'end_of_quarter':
      d = new Date(Date.UTC(d.getUTCFullYear(), Math.floor(d.getUTCMonth() / 3) * 3 + 3, 0));
      break;
    case 'start_of_year':
      d = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      break;
    case 'end_of_year':
      d = new Date(Date.UTC(d.getUTCFullYear(), 11, 31));
      break;
  }
  const offsets = m[2] ?? '';
  for (const part of offsets.matchAll(/([+-])(\d{1,4})([hdwmqy])/g)) {
    const sign = part[1] === '-' ? -1 : 1;
    const n = Number(part[2]) * sign;
    switch (part[3]) {
      case 'h':
        d = new Date(d.getTime() + n * 3_600_000);
        break;
      case 'd':
        d = new Date(d.getTime() + n * 86_400_000);
        break;
      case 'w':
        d = new Date(d.getTime() + n * 7 * 86_400_000);
        break;
      case 'm':
        d = addMonthsUtc(d, n);
        break;
      case 'q':
        d = addMonthsUtc(d, n * 3);
        break;
      case 'y':
        d = addMonthsUtc(d, n * 12);
        break;
    }
  }
  return d;
}

function addMonthsUtc(d: Date, months: number): Date {
  const target = new Date(d.getTime());
  const day = target.getUTCDate();
  target.setUTCDate(1);
  target.setUTCMonth(target.getUTCMonth() + months);
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(day, lastDay));
  return target;
}

// ---------------------------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------------------------

export const SortDirection = z.enum(['asc', 'desc']);
export const SortSpec = z.object({ field: FieldPath, dir: SortDirection.default('asc') }).strict();
export type SortSpec = z.infer<typeof SortSpec>;
